/**
 * Standalone HTML export: the sharing and reading artefact, distinct from the
 * `.json` save format (implementation-plan.md §6).
 *
 * One file, no script, no network: the chart, its styles and the print rules,
 * ready to open anywhere — including on a phone, where editing was never the
 * point. It is laid out at print geometry, so what is on screen is what comes
 * out of the printer.
 */

import printCss from '../styles/print.css?inline';
import timelineCss from '../styles/timeline.css?inline';
import tokensCss from '../styles/tokens.css?inline';

import type { ChronoDocument } from '../model/types';
import { layoutDocument } from '../render/layout';
import { PRINT_METRICS } from '../render/metrics';
import {
  DEFAULT_PRINT_SETTINGS,
  pageGeometry,
  printLayoutOptions,
  type PrintSettings,
} from '../render/print';
import { renderTimeline } from '../render/timeline';

const escapeHtml = (text: string): string =>
  text.replace(/[&<>"]/g, (char) => `&${{ '&': 'amp', '<': 'lt', '>': 'gt', '"': 'quot' }[char]};`);

/** The document chrome, which lives in app.css alongside editor styles we do not want here. */
const CHROME_CSS = `
body {
  font-family: system-ui, sans-serif;
  background: var(--paper);
  color: var(--ink);
  margin: 24px;
}
h1 { font-size: 18px; margin: 0 0 16px; }
#legend .swatch { display: inline-flex; align-items: center; gap: 6px; }
`;

function legendHtml(doc: ChronoDocument): string {
  const swatches = Object.values(doc.categories)
    .map(
      (category) =>
        `<span class="swatch"><i style="background:${escapeHtml(category.color)}"></i>` +
        `${escapeHtml(category.name)}</span>`,
    )
    .join('');
  return `<div id="legend">${swatches}</div>`;
}

export function buildStandaloneHtml(
  doc: ChronoDocument,
  title = 'Chrono-Arrow',
  settings: PrintSettings = DEFAULT_PRINT_SETTINGS,
): string {
  const metrics = PRINT_METRICS;
  const page = pageGeometry(settings);

  const container = document.createElement('div');
  container.id = 'bands';
  renderTimeline(container, doc, layoutDocument(doc, printLayoutOptions(settings)), metrics);

  const vars = Object.entries({
    '--px-per-tick': metrics.pxPerTick,
    '--period-lane-h': metrics.periodLaneH,
    '--event-lane-h': metrics.eventLaneH,
    '--bar-gap': metrics.barGap,
    '--band-gap': metrics.bandGap,
    '--label-row-h': metrics.labelRowH,
    '--band-label-h': metrics.bandLabelH,
    '--gutter-w': metrics.gutterW,
  })
    .map(([name, value]) => `  ${name}: ${value};`)
    .join('\n');

  // The export carries its own page rule: it is a file that gets printed
  // elsewhere, with no app around it to inject one.
  const pageRule =
    `@page { size: A4 ${settings.orientation}; ` +
    `margin: ${page.marginBlockMm}mm ${page.marginInlineMm}mm; }`;

  return `<!doctype html>
<html lang="fr">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${escapeHtml(title)}</title>
<style>
${tokensCss}
:root {
${vars}
}
${CHROME_CSS}
${timelineCss}
${printCss}
${pageRule}
</style>
</head>
<body>
<h1>${escapeHtml(title)}</h1>
${legendHtml(doc)}
${container.outerHTML}
</body>
</html>
`;
}
